/**
 * The step-up list across a reload (ADR-068).
 *
 * Refused step-up actions waiting for a fresh sign-in ("Waiting for a fresh
 * sign-in", ADR-062) are kept in `sessionStorage` — the tab's own storage,
 * gone with the tab — one entry per workspace under
 * `gp.stepUp.v<STEP_UP_STORE_VERSION>.<workspace id>`, each recording the
 * user it belongs to. This is not mock state: it is written whether or not
 * the mock keeps its own state (`mockPersistence.ts`), in every build.
 *
 * What an entry keeps: the refusal id, the workspace and brand ids, the
 * account and workspace names and the action's wording the list restates,
 * when it was refused, whether it was `rethrown`, whether a mutation had
 * claimed it, and — only for an action that may be sent again — its
 * {@link StepUpRequest}: method, path, `Idempotency-Key`, `If-Match`, and a
 * body only when every field is on its action's list (`stepUpRequestOf`).
 * Never a token, a secret, a response, or a body field nobody listed.
 *
 * Read back only for the user who wrote it: an entry of another user, of
 * another schema version, or one that does not parse is removed unread.
 * Signing out removes every entry ({@link forgetStoredStepUps}).
 */
import { IDEMPOTENCY_KEY_LIFETIME_MS } from '@/lib/env';
import type { StepUpAction, StepUpEntry, StepUpRequest } from './stepUp';

/**
 * How long the server keeps an `Idempotency-Key` bound to its first request,
 * in milliseconds: the session's `idempotency_key_retention_hours` (`GET
 * /me`, the deployment's effective `RETENTION_IDEMPOTENCY_KEYS_HOURS`,
 * ADR-074). A waiting step-up action at least this old is `expired` and is
 * never sent again. Only when the field is absent (an older server) or not a
 * whole number of hours from 1 to 720 does the browser fall back to the
 * server's 24-hour default, {@link IDEMPOTENCY_KEY_LIFETIME_MS}.
 */
export function idempotencyKeyLifetimeMs(
  session: { readonly idempotency_key_retention_hours?: number } | null | undefined,
): number {
  const hours = session?.idempotency_key_retention_hours;
  return typeof hours === 'number' && Number.isInteger(hours) && hours >= 1 && hours <= 720
    ? hours * 60 * 60 * 1000
    : IDEMPOTENCY_KEY_LIFETIME_MS;
}

/** Every key this module writes starts with it, whatever its version. */
export const STEP_UP_STORE_PREFIX = 'gp.stepUp.';
/** The schema of a stored list; a list of any other version is removed unread. */
export const STEP_UP_STORE_VERSION = 1;
const VERSIONED = `${STEP_UP_STORE_PREFIX}v${STEP_UP_STORE_VERSION}.`;

/**
 * How long a "send after the sign-in" mark is honoured: the sign-in's single
 * use `state` lives ten minutes (`/auth/login`), plus slack. A mark found
 * later is an abandoned sign-in, and the action just waits again.
 */
export const STEP_UP_RESUME_WINDOW_MS = 15 * 60 * 1000;

/** The storage key of one workspace's list. */
export function stepUpStoreKey(workspaceId: string | null | undefined): string {
  return `${VERSIONED}${workspaceId ?? '_'}`;
}

/** One stored action. */
interface StoredStepUp {
  readonly id: string;
  readonly workspaceId: string | null;
  readonly brandId: string | null;
  readonly account: string | null;
  readonly workspace: string | null;
  readonly action: StepUpAction;
  readonly createdAt: number;
  readonly rethrown: boolean;
  readonly bound: boolean;
  readonly request: StepUpRequest | null;
  /** Set when the person signed in through a navigation: send once on return. */
  readonly resumeAt?: number;
}

interface StoredList {
  readonly user: string;
  readonly entries: readonly StoredStepUp[];
}

function storage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function keysWith(store: Storage, prefix: string): string[] {
  const keys: string[] = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key?.startsWith(prefix)) keys.push(key);
  }
  return keys;
}

const isString = (v: unknown): v is string => typeof v === 'string';
const isOptString = (v: unknown): v is string | null => v === null || typeof v === 'string';
const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function stringRecord(v: unknown): Record<string, string> | null {
  if (!isRecord(v)) return null;
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(v)) {
    if (!isString(value)) return null;
    out[key] = value;
  }
  return out;
}

function parseRequest(v: unknown): StepUpRequest | null | undefined {
  if (v === null) return null;
  if (!isRecord(v) || !isString(v['method']) || !isString(v['path'])) return undefined;
  if (!v['path'].startsWith('/api/v1/workspaces/')) return undefined;
  const body = v['body'] === undefined ? undefined : stringRecord(v['body']);
  if (body === null) return undefined;
  const key = v['idempotencyKey'];
  const ifMatch = v['ifMatchVersion'];
  if ((key !== undefined && !isString(key)) || (ifMatch !== undefined && !isString(ifMatch)))
    return undefined;
  return {
    method: v['method'],
    path: v['path'],
    ...(body ? { body } : {}),
    ...(key ? { idempotencyKey: key } : {}),
    ...(ifMatch ? { ifMatchVersion: ifMatch } : {}),
  };
}

function parseEntry(v: unknown): StoredStepUp | null {
  if (!isRecord(v)) return null;
  const action = v['action'];
  if (!isRecord(action) || !isString(action['key'])) return null;
  if (!action['key'].startsWith('auth.stepUp.action.')) return null;
  const values = stringRecord(action['values']);
  if (values === null) return null;
  if (action['role'] !== undefined && !isString(action['role'])) return null;
  const request = parseRequest(v['request']);
  if (request === undefined) return null;
  if (
    !isString(v['id']) ||
    !isOptString(v['workspaceId']) ||
    !isOptString(v['brandId']) ||
    !isOptString(v['account']) ||
    !isOptString(v['workspace']) ||
    typeof v['createdAt'] !== 'number' ||
    !Number.isFinite(v['createdAt']) ||
    typeof v['rethrown'] !== 'boolean' ||
    typeof v['bound'] !== 'boolean' ||
    (v['resumeAt'] !== undefined && typeof v['resumeAt'] !== 'number')
  ) {
    return null;
  }
  return {
    id: v['id'],
    workspaceId: v['workspaceId'],
    brandId: v['brandId'],
    account: v['account'],
    workspace: v['workspace'],
    action: {
      key: action['key'] as StepUpAction['key'],
      values,
      ...(isString(action['role']) ? { role: action['role'] as StepUpAction['role'] } : {}),
    } as StepUpAction,
    createdAt: v['createdAt'],
    rethrown: v['rethrown'],
    bound: v['bound'],
    request,
    ...(typeof v['resumeAt'] === 'number' ? { resumeAt: v['resumeAt'] } : {}),
  };
}

function toStored(entry: StepUpEntry, now: number, resumeAt?: number): StoredStepUp {
  return {
    id: entry.id,
    workspaceId: entry.workspaceId ?? null,
    brandId: entry.brandId ?? null,
    account: entry.account,
    workspace: entry.workspace,
    action: entry.action,
    createdAt: entry.createdAt ?? now,
    rethrown: entry.rethrown === true,
    bound: entry.mutation !== null || entry.restored?.bound === true,
    request: entry.request ?? null,
    ...(resumeAt === undefined ? {} : { resumeAt }),
  };
}

function fromStored(stored: StoredStepUp): StepUpEntry {
  return {
    id: stored.id,
    account: stored.account,
    workspace: stored.workspace,
    workspaceId: stored.workspaceId,
    brandId: stored.brandId,
    action: stored.action,
    mutation: null,
    ...(stored.rethrown ? { rethrown: true } : {}),
    createdAt: stored.createdAt,
    request: stored.request,
    restored: { bound: stored.bound },
  };
}

/**
 * Writes `entries` (waiting, in order) for `user`, one list per workspace,
 * and removes every stored list they no longer fill. Entries whose id is in
 * `resume` are marked to be sent once when the page comes back from a
 * sign-in (at `now`). Storage that throws is never an error: the list then
 * lives as long as the page, as before ADR-068.
 */
export function saveStepUps(
  user: string,
  entries: readonly StepUpEntry[],
  now: number,
  resume: ReadonlySet<string> = new Set(),
): void {
  const store = storage();
  if (!store) return;
  try {
    const lists = new Map<string, StoredStepUp[]>();
    for (const entry of entries) {
      const key = stepUpStoreKey(entry.workspaceId);
      const list = lists.get(key) ?? [];
      list.push(toStored(entry, now, resume.has(entry.id) ? now : undefined));
      lists.set(key, list);
    }
    for (const key of keysWith(store, STEP_UP_STORE_PREFIX)) {
      if (!lists.has(key)) store.removeItem(key);
    }
    for (const [key, list] of lists) {
      store.setItem(key, JSON.stringify({ user, entries: list } satisfies StoredList));
    }
  } catch {
    // Blocked or over quota: the page keeps the list in memory.
  }
}

/**
 * The actions stored for `user`, oldest first, and those of them marked to
 * be sent once now that the page is back from a sign-in (within
 * {@link STEP_UP_RESUME_WINDOW_MS} of `now`; the mark is consumed: the
 * caller saves the list again). Lists of another user or schema version,
 * and anything that does not parse, are removed unread.
 */
export function loadStepUps(
  user: string,
  now: number,
): { readonly entries: readonly StepUpEntry[]; readonly resume: ReadonlySet<string> } {
  const store = storage();
  const empty = { entries: [], resume: new Set<string>() };
  if (!store) return empty;
  try {
    const found: StoredStepUp[] = [];
    for (const key of keysWith(store, STEP_UP_STORE_PREFIX)) {
      if (!key.startsWith(VERSIONED)) {
        store.removeItem(key);
        continue;
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(store.getItem(key) ?? 'null');
      } catch {
        parsed = null;
      }
      if (!isRecord(parsed) || parsed['user'] !== user || !Array.isArray(parsed['entries'])) {
        store.removeItem(key);
        continue;
      }
      for (const raw of parsed['entries']) {
        const entry = parseEntry(raw);
        if (entry && !found.some((e) => e.id === entry.id)) found.push(entry);
      }
    }
    found.sort((a, b) => a.createdAt - b.createdAt);
    const resume = new Set(
      found
        .filter((e) => e.resumeAt !== undefined && now - e.resumeAt <= STEP_UP_RESUME_WINDOW_MS)
        .map((e) => e.id),
    );
    return { entries: found.map(fromStored), resume };
  } catch {
    return empty;
  }
}

/** Removes every stored step-up list (sign-out). */
export function forgetStoredStepUps(): void {
  const store = storage();
  if (!store) return;
  try {
    for (const key of keysWith(store, STEP_UP_STORE_PREFIX)) store.removeItem(key);
  } catch {
    // Storage blocked: nothing was stored.
  }
}
